// frontend/src/components/ui/notification-trigger.jsx
import React, { useState } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { Bell, BellDot } from 'lucide-react';
import { cn } from '../../lib/utils';

const NotificationTrigger = ({ 
  variant = 'icon', 
  className = '',
  showBadge = true,
  children,
  ...props 
}) => {
  const { notifications, unreadCount, openNotificationCenter } = useNotifications();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    openNotificationCenter();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openNotificationCenter();
    }
  };

  const hasUnread = unreadCount > 0;

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'relative p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg focus:outline-none focus:ring-2 transition-colors',
          className
        )}
        style={{ '--tw-ring-color': 'var(--brand-primary)' }}
        title={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ''}`}
        {...props}
      >
        {hasUnread ? (
          <BellDot className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />
        ) : (
          <Bell className="h-5 w-5" />
        )}
        
        {showBadge && hasUnread && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-lg hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors',
          className
        )}
        {...props}
      >
        {hasUnread ? (
          <BellDot className="h-4 w-4 text-brand-primary" />
        ) : (
          <Bell className="h-4 w-4" />
        )}
        <span>Notifications</span>
        {showBadge && hasUnread && (
          <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  if (variant === 'badge') {
    return (
      <div
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        className={cn(
          'relative inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full focus:outline-none focus:ring-2 focus:ring-brand-primary transition-colors cursor-pointer',
          className
        )}
        title={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ''}`}
        {...props}
      >
        {hasUnread ? (
          <BellDot className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />
        ) : (
          <Bell className="h-5 w-5" />
        )}
        
        {showBadge && hasUnread && (
          <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </div>
    );
  }

  // Default: render children with click handler
  return (
    <div 
      onClick={handleClick} 
      onKeyDown={handleKeyDown} 
      role="button" 
      tabIndex={0}
      className="relative"
      {...props}
    >
      {children}
      {showBadge && hasUnread && (
        <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </div>
  );
};

export default NotificationTrigger;